import * as React from 'react';
import {Box, Typography} from '@mui/material';
import {HtmlTemplateLocalization} from '../models/dtos/HtmlTemplateDto';

interface HtmlTemplatePreviewProps {
    htmlContent: string;
    localizations?: HtmlTemplateLocalization[];
    language?: string;
    betcoinsMacrosKey?: string | null;
    expirationTimestampMacrosKey?: string | null;
    discountMacroKey?: string | null;
    oldPriceKeys?: string[];
    newPriceKeys?: string[];
    betcoins?: number;
    expirationTimestamp?: number;
    discount?: number;
    oldPrice?: number;
    newPrice?: number;
    width?: number | string;
    height?: number | string;
    borderRadius?: string;
    borderWidth?: string;
    interactive?: boolean;
    showLabel?: boolean;
}

function escapeForJs(str: string): string {
    return str
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/\n/g, '\\n');
}

function buildPreviewHtml(
    htmlContent: string,
    localizations?: HtmlTemplateLocalization[],
    language?: string,
    betcoinsMacrosKey?: string | null,
    expirationTimestampMacrosKey?: string | null,
    discountMacroKey?: string | null,
    oldPriceKeys?: string[],
    newPriceKeys?: string[],
    betcoins?: number,
    expirationTimestamp?: number,
    discount?: number,
    oldPrice?: number,
    newPrice?: number
): string {
    if (!htmlContent) return '';

    let processed = htmlContent;

    // Replace macros in the HTML content ({{key}} -> value)
    if (betcoinsMacrosKey && betcoins !== undefined) {
        const macrosPattern = new RegExp(`\\{\\{${betcoinsMacrosKey.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\}\\}`, 'g');
        processed = processed.replace(macrosPattern, betcoins.toLocaleString());
    }
    if (expirationTimestampMacrosKey && expirationTimestamp !== undefined) {
        const macrosPattern = new RegExp(`\\{\\{${expirationTimestampMacrosKey.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\}\\}`, 'g');
        processed = processed.replace(macrosPattern, String(expirationTimestamp));
    }
    if (discountMacroKey && discount !== undefined) {
        const clampedDiscount = Math.min(1.0, Math.max(0.0, discount));
        const discountPercent = Math.round(clampedDiscount * 100) + '%';
        const macrosPattern = new RegExp(`\\{\\{${discountMacroKey.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\}\\}`, 'g');
        processed = processed.replace(macrosPattern, discountPercent);
    }

    const lang = language || 'en';
    const assignments: string[] = [];

    // Localization assignments by element ID
    if (localizations && localizations.length > 0) {
        localizations
            .filter(loc => loc.id)
            .forEach(loc => {
                const value = loc.localizations[lang] ?? loc.localizations['en'] ?? '';
                const escapedValue = escapeForJs(value);
                const escapedId = escapeForJs(loc.id || '');
                assignments.push(`var el = document.getElementById('${escapedId}'); if (el) { el.textContent = '${escapedValue}'; }`);
            });
    }

    // Old price key assignments
    const effectiveOldPrice = oldPrice ?? 19.99;
    if (oldPriceKeys && oldPriceKeys.length > 0) {
        oldPriceKeys.forEach(key => {
            const escapedId = escapeForJs(key);
            assignments.push(`var el = document.getElementById('${escapedId}'); if (el) { el.textContent = '$${effectiveOldPrice.toFixed(2)}'; }`);
        });
    }

    // New price key assignments
    const effectiveNewPrice = newPrice ?? 9.99;
    if (newPriceKeys && newPriceKeys.length > 0) {
        newPriceKeys.forEach(key => {
            const escapedId = escapeForJs(key);
            assignments.push(`var el = document.getElementById('${escapedId}'); if (el) { el.textContent = '$${effectiveNewPrice.toFixed(2)}'; }`);
        });
    }

    if (assignments.length === 0) return processed;

    const script = `<script>document.addEventListener('DOMContentLoaded', function() {\n${assignments.join('\n')}\n});</script>`;

    if (processed.includes('</body>')) {
        return processed.replace('</body>', `${script}</body>`);
    } else if (processed.includes('</html>')) {
        return processed.replace('</html>', `${script}</html>`);
    } else {
        return processed + script;
    }
}

export default function HtmlTemplatePreview({
    htmlContent,
    localizations,
    language = 'en',
    betcoinsMacrosKey,
    expirationTimestampMacrosKey,
    discountMacroKey,
    oldPriceKeys,
    newPriceKeys,
    betcoins = 100000,
    expirationTimestamp = Math.floor(Date.now() / 1000) + 86400,
    discount = 0.5,
    oldPrice = 19.99,
    newPrice = 9.99,
    width = 375,
    height,
    borderRadius = '24px',
    borderWidth = '2px',
    interactive = false,
    showLabel = false
}: HtmlTemplatePreviewProps) {
    const previewHtml = React.useMemo(
        () => buildPreviewHtml(
            htmlContent, localizations, language,
            betcoinsMacrosKey, expirationTimestampMacrosKey, discountMacroKey,
            oldPriceKeys, newPriceKeys,
            betcoins, expirationTimestamp, discount, oldPrice, newPrice
        ),
        [htmlContent, localizations, language, betcoinsMacrosKey, expirationTimestampMacrosKey, discountMacroKey, oldPriceKeys, newPriceKeys, betcoins, expirationTimestamp, discount, oldPrice, newPrice]
    );

    const sizeStyles: Record<string, any> = {width};
    if (height) {
        sizeStyles.height = height;
    } else {
        sizeStyles.aspectRatio = '9 / 16';
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {showLabel && (
                <Typography variant="caption" color="text.secondary" sx={{mb: 1}}>
                    Live Preview (mobile)
                </Typography>
            )}
            <Box sx={{
                ...sizeStyles,
                borderRadius,
                border: `${borderWidth} solid #444`,
                overflow: 'hidden',
                background: 'transparent'
            }}>
                {htmlContent ? (
                    <iframe
                        title="HTML Template Preview"
                        srcDoc={previewHtml}
                        sandbox={(localizations && localizations.length > 0) || (oldPriceKeys && oldPriceKeys.length > 0) || (newPriceKeys && newPriceKeys.length > 0) || betcoinsMacrosKey || expirationTimestampMacrosKey || discountMacroKey || interactive ? 'allow-scripts' : ''}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: 'block',
                            background: 'transparent',
                            pointerEvents: interactive ? 'auto' : 'none'
                        }}
                    />
                ) : (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        color: 'text.disabled'
                    }}>
                        <Typography variant="body2" textAlign="center" sx={{px: 3}}>
                            No HTML content
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
